import { createAppWithRootComponent, OptionalVueProp, VueEmit, VueProp } from "../core/vueHelpers";
import { defineComponent, VNode, nextTick } from "vue";
import { ExternalLinkCardProps } from "./types";
import { ButtonDefinition, VueContext } from "../../types";
import Button from "../core/button";
import Svg from "../core/svg";
import { Key as SvgKey, IconType } from "../../types/skyward/svg";
import DefaultSectionHeader from "../skyVue/browse/components/default/defaultSectionHeader";


export interface ExternalLinkCardContext extends VueContext
{
    containerData: any;
}

const rootComponent = defineComponent({
    name: "ExternalLinkCard",
    props:
    {
        container: VueProp<JQuery<HTMLElement>>(Object),
        applicationid: OptionalVueProp(Number),
        externallinkid: OptionalVueProp(Number),
        name: VueProp(String),
        description: VueProp(String),
        action: VueProp(String),
        iconurl: VueProp(String),
        url: VueProp(String),
        iconcode: VueProp<SvgKey>(String as () => SvgKey),
        ispinned: VueProp(Boolean),
        showpin: {
            type: Boolean,
            required: false,
            default: true
        },
    },
    render(): VNode
    {
        return (
            <div class="externalLinkCardContent" onClick={() => this.openLink()}>
                {
                    this.applicationid &&
                    <a role="button" class="externalLinkCardCornerIcon externalLinkCardKeyIcon" href="javascript:void(0)">
                        <Svg name="Key"
                            svgClasses={["buttonSvg", "externalLinkCardCornerIcon", "no-hover"]}
                            tabindex="-1" />
                    </a>
                }
                {
                    this.showpin &&
                    <a role="button" onClick={(e) => this.togglePinButtonClick(e)} class="externalLinkCardCornerIcon externalLinkCardPinIcon" href="javascript:void(0)">
                        <Svg name={this.pinnedStatus ? "PinFilled" : "Pin"}
                            svgClasses={["buttonSvg", "externalLinkCardCornerIcon", "no-hover"]}
                            tabindex="-1" />
                    </a>
                }
                {this.getCardBody()}
            </div>
        );
    },
    data()
    {
        return {
            pinnedStatus: this.ispinned
        };
    },
    computed: {
        smallTileBodyClasses()
        {
            let classes = "widgetTileBody";

            if (this.iconcode)
            {
                classes += " widgetTileBorderHolder";
            }

            return classes;
        }
    }
    ,
    methods:
    {
        getCardBody(): VNode
        {
            if (this.description)
            {
                return this.getDescriptionCard();
            }
            else
            {
                return this.getIconCard();
            }
        },
        getDescriptionCard(): VNode
        {
            return (
                <div class="widgetWideContent">
                    <div class="widgetWideContentInner">
                        <div class="widgetWideTileBody" aria-label="App with automatic sign-in">
                            {this.getIcon()}
                        </div>
                        <div class="widgetWideTileFooter">
                            <div class="widgetTileFooterLabel">
                                <span class="widgetTileFooterText">{this.name}</span>
                            </div>
                            <span class="externalLinkCardDescription">{this.description}</span>
                        </div>
                    </div>
                </div>
            );
        },
        getIconCard(): VNode
        {
            return (
                <div>
                    <div class={this.smallTileBodyClasses} aria-label="App with automatic sign-in">
                        {this.getIcon()}
                    </div>
                    <div class="widgetTileFooter">
                        <div class="widgetTileFooterLabel">
                            <span class="widgetTileFooterText">{this.name}</span>
                        </div>
                    </div>
                </div>
            );
        },
        getIcon(): VNode
        {
            if (this.iconcode)
            {
                return (
                    <Svg name={this.iconcode} class="svgWidget" preserveAspectRatio="xMidYMin meet"></Svg>
                );
            } else
            {
                return (
                    <img src={this.iconurl} class="svgWidget widgetIcon" />
                );
            }
        },
        async togglePinButtonClick(e: Event)
        {
            e.stopImmediatePropagation();
            await this.togglePin();
        },
        async togglePin()
        {
            let data = {} as any;

            data.pin = !this.pinnedStatus;
            this.pinnedStatus = !this.pinnedStatus;

            if (this.applicationid)
            {
                data.applicationID = this.applicationid;
            } else
            {
                data.externalLinkID = this.externallinkid;
            }

            const result = await $.ajax({
                url: `${window.skyward.global.virtualDir}Security/PinnedApplication/PinExternalLink`,
                data: data,
                type: "POST",
                dataType: "JSON"
            });

            if (result.success)
            {
                this.pinnedStatus = result.isPinned ?? false;
            }
        },
        openLink()
        {
            let url = this.url;

            if (!url.startsWith("http") && !url.startsWith("/"))
            {
                url = `//${url}`;
            }

            window.open(url);
        }
    }
});

export const ExternalLinkCard =
{
    buildVue(context: ExternalLinkCardContext)
    {
        const vm = createAppWithRootComponent(rootComponent,
            {
                ...context.containerData,
                container: $(context.container),
                isMobile: context.isMobile
            })
            .mount(context.container);

        return vm;
    }
};
